import React, { FC, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../hooks/useAppDispatcher.hook';
import { getAttachmentDownloadThunk } from '../../redux/Attachment/Attachment.thunk';
import { Attachment } from '../../redux/Attachment/Attachment.type';
import { ATTACHMENTS_ICONS } from '../Icons/Attachments.icons';
import toTitle from '../../utils/toTitle';
import ButtonComponent from '../Button.component';
import SpinnerComponent from '../Spinner.component';
import DownloadIcon from '../Icons/Download.icon';
import ShareIcon from '../Icons/Share.icon';
import dayjs from 'dayjs';

export interface AttachmentsBlockProps {
  attachments: Attachment[];
  wrap?: boolean;
  withAllWidth?: boolean;
}

type ActionType = 'download' | 'preview';

const StyledInfoAttachmentsWrapper = styled.div<{
  alignStart: boolean;
}>`
  display: grid;
  max-height: 300px;
  overflow-y: auto;
  gap: 10px;

  ${(props: { alignStart: boolean }) =>
    !props.alignStart &&
    css`
      justify-content: flex-start;
    `};
`;

const StyledButtonContentWrapper = styled.div<{ withAllWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  ${props => props.withAllWidth && 'min-width: 210px'}
`;

const StyledInfoAttachmentsBox = styled.div`
  display: flex;
  gap: 10px;
  padding: 5px;
  align-items: center;
  margin-bottom: 10px;
`;

const StyledInfoAttachmentsTitle = styled.div`
  margin-bottom: 5px;
`;

const StyledButtonsWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin-left: auto;
`;

const StyledSubText = styled.div`
  font-weight: 300;
  word-break: break-all;
`;

const AttachmentsBlockComponent: FC<AttachmentsBlockProps> = ({
  attachments,
  wrap = true,
  withAllWidth,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [action, setAction] = useState<ActionType | undefined>(undefined);

  const sortedAttachments = useMemo(
    () =>
      [...attachments].sort(
        (a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf(),
      ),
    [attachments],
  );
  const onPreview = (payload: Blob) => {
    const url = URL.createObjectURL(
      new Blob([payload], { type: payload.type }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.click();
    link.parentNode?.removeChild(link);
  };

  const onDownload = (attachment: Attachment, payload: Blob) => {
    const url = URL.createObjectURL(new Blob([payload]));
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.url.substring(
      attachment.url.lastIndexOf('/') + 1,
    );
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  };

  const actionClick = async (attachment: Attachment, type: ActionType) => {
    if (!attachment.isCloud) {
      return window.open(attachment.url, '_blank');
    }

    setLoading(prev => ({ ...prev, [attachment.uuid]: true }));

    try {
      const { payload } = await dispatch(
        getAttachmentDownloadThunk({
          id: attachment.uuid,
        }),
      );

      if (type === 'download') {
        onDownload(attachment, payload);
        return;
      }

      onPreview(payload);
    } finally {
      setLoading(prev => ({ ...prev, [attachment.uuid]: false }));
    }
  };

  return (
    <StyledInfoAttachmentsWrapper alignStart={wrap}>
      {sortedAttachments.map((attachment: Attachment) => (
        <StyledInfoAttachmentsBox key={attachment.uuid}>
          <div>{ATTACHMENTS_ICONS[attachment.type]}</div>
          <StyledButtonContentWrapper withAllWidth={withAllWidth}>
            <StyledInfoAttachmentsTitle>
              {t(toTitle(attachment.type))}
            </StyledInfoAttachmentsTitle>
            {attachment.url && (
              <StyledSubText>
                {attachment.isCloud
                  ? attachment.url.substring(
                      attachment.url.lastIndexOf('/') + 1,
                    )
                  : attachment.url}
              </StyledSubText>
            )}
          </StyledButtonContentWrapper>

          <StyledButtonsWrapper>
            <ButtonComponent
              disabled={
                !!Object.keys(loading).find(
                  (key: string) => loading[key] === true,
                )
              }
              handleClick={() => {
                setAction('preview');
                actionClick(attachment, 'preview');
              }}
            >
              {loading[attachment.uuid] && action === 'preview' ? (
                <SpinnerComponent size="small" />
              ) : (
                <ShareIcon />
              )}
            </ButtonComponent>

            <ButtonComponent
              disabled={
                !!Object.keys(loading).find(
                  (key: string) => loading[key] === true,
                )
              }
              handleClick={() => {
                setAction('download');
                actionClick(attachment, 'download');
              }}
            >
              {loading[attachment.uuid] && action === 'download' ? (
                <SpinnerComponent size="small" />
              ) : (
                <DownloadIcon />
              )}
            </ButtonComponent>
          </StyledButtonsWrapper>
        </StyledInfoAttachmentsBox>
      ))}
    </StyledInfoAttachmentsWrapper>
  );
};

export default AttachmentsBlockComponent;
